import { useCallback, useEffect, useMemo, useState, type ChangeEvent } from 'react'
import { Button, Card, CardContent } from '@saas-pages/shared/ui'
import { startToolOperation, trackToolRejection, type ToolAnalyticsContext } from '@saas-pages/shared/public/runtime'
import { localeCode, useLocale, useT } from '@/shared/i18n'
import { routePath } from '@/seo/routes'
import {
  CURATED_TIME_ZONES,
  buildShareHash,
  formatOffset,
  getSsgInitialTimeZoneState,
  normalizeTimeZone,
  parseSharedTimeZoneState,
  resolveZonedInstant,
  resolveCuratedTimeZone,
  shouldHydrateTimeZoneFromBrowser,
  formatInstant,
  type TimeZoneState,
} from '@/pages/time-zones/model/timeZones'

const UTILITY = 'time-zone-converter'
const ANALYTICS_CONTEXT = {
  app: 'dateify',
  domain: 'dateify.ru',
  route: '/chasovye-poyasa',
  utility: UTILITY,
  processingMode: 'local',
} satisfies ToolAnalyticsContext

export function TimeZonesPage() {
  const t = useT()
  const { locale } = useLocale()
  const [state, setState] = useState<TimeZoneState>(getSsgInitialTimeZoneState)
  const [sourceQuery, setSourceQuery] = useState('UTC')
  const [zoneQuery, setZoneQuery] = useState('')
  const [shareStatus, setShareStatus] = useState<'idle' | 'copied'>('idle')

  useEffect(() => {
    if (!shouldHydrateTimeZoneFromBrowser(window.navigator.userAgent)) return
    const hydratedState = parseSharedTimeZoneState(
      window.location.hash,
      Intl.DateTimeFormat().resolvedOptions().timeZone,
    )
    setState(hydratedState)
    setSourceQuery(hydratedState.sourceZone)
  }, [])

  const normalizedSourceZone = useMemo(() => normalizeTimeZone(sourceQuery), [sourceQuery])
  const resolution = useMemo(
    () => resolveZonedInstant(state.date, state.time, normalizedSourceZone ?? '', state.disambiguation),
    [normalizedSourceZone, state.date, state.disambiguation, state.time],
  )
  const instant = resolution.instant

  const update = useCallback(<Key extends keyof TimeZoneState>(key: Key, value: TimeZoneState[Key]) => {
    setState((current) => ({ ...current, [key]: value }))
  }, [])

  const handleSourceChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    setSourceQuery(value)
    const normalized = normalizeTimeZone(value)
    if (normalized) update('sourceZone', normalized)
  }, [update])

  const canonicalizeSource = useCallback(() => {
    const normalized = normalizeTimeZone(sourceQuery)
    if (!normalized) return
    setSourceQuery(normalized)
    update('sourceZone', normalized)
  }, [sourceQuery, update])

  const addZone = useCallback(() => {
    const zone = normalizeTimeZone(resolveCuratedTimeZone(zoneQuery) ?? zoneQuery)
    if (!zone || state.zones.includes(zone) || state.zones.length >= 5) {
      trackToolRejection(ANALYTICS_CONTEXT, 'invalid_input')
      return
    }
    const startedAt = performance.now()
    update('zones', [...state.zones, zone])
    setZoneQuery('')
    startToolOperation(ANALYTICS_CONTEXT).success(Math.max(0, performance.now() - startedAt))
  }, [state.zones, update, zoneQuery])

  const removeZone = useCallback((zone: string) => {
    if (state.zones.length <= 2) return
    const startedAt = performance.now()
    update('zones', state.zones.filter((item) => item !== zone))
    startToolOperation(ANALYTICS_CONTEXT).success(Math.max(0, performance.now() - startedAt))
  }, [state.zones, update])

  const moveZone = useCallback((index: number, direction: -1 | 1) => {
    const target = index + direction
    if (target < 0 || target >= state.zones.length) return
    const next = [...state.zones]
    ;[next[index], next[target]] = [next[target]!, next[index]!]
    const startedAt = performance.now()
    update('zones', next)
    startToolOperation(ANALYTICS_CONTEXT).success(Math.max(0, performance.now() - startedAt))
  }, [state.zones, update])

  const copyShareLink = useCallback(() => {
    if (resolution.status !== 'ready' && resolution.status !== 'ambiguous') return
    const startedAt = performance.now()
    const operation = startToolOperation(ANALYTICS_CONTEXT)
    const origin = typeof window === 'undefined' ? 'https://dateify.ru' : window.location.origin
    const url = `${origin}${routePath['time-zones']}${buildShareHash(state)}`
    let clipboard: Clipboard | undefined
    try {
      clipboard = typeof navigator === 'undefined' ? undefined : navigator.clipboard
    } catch {
      operation.error('processing_failed')
      return
    }
    if (!clipboard?.writeText) {
      operation.error('unsupported_input')
      return
    }
    let write: Promise<void>
    try {
      write = Promise.resolve(clipboard.writeText(url))
    } catch {
      operation.error('processing_failed')
      return
    }
    write.then(() => {
      setShareStatus('copied')
      window.setTimeout(() => setShareStatus('idle'), 1500)
      operation.success(Math.round(performance.now() - startedAt))
    }).catch(() => operation.error('processing_failed'))
  }, [resolution.status, state])

  const zoneLabel = useCallback((zone: string) => {
    const entry = CURATED_TIME_ZONES.find((item) => item.zone === zone)
    return entry ? (locale === 'ru' ? entry.ru : entry.en) : zone
  }, [locale])

  return (
    <div className="mx-auto max-w-5xl">
      <section className="pb-4 pt-1 sm:pb-8 sm:pt-4">
        <div className="mb-2 inline-flex rounded-md border bg-card px-2 py-0.5 text-xs font-medium text-muted-foreground shadow-sm sm:mb-3 sm:px-2.5 sm:py-1">
          {t.common.localBadge}
        </div>
        <h1 className="text-2xl font-bold tracking-normal sm:text-4xl">{t.timeZones.title}</h1>
        <p className="mt-1 line-clamp-2 max-w-3xl text-sm leading-6 text-muted-foreground sm:mt-2 sm:line-clamp-none sm:text-base sm:leading-7">
          {t.timeZones.description}
        </p>
      </section>

      <Card className="ym-hide-content ym-disable-keys overflow-hidden">
        <div aria-hidden="true" className="h-1.5 bg-gradient-to-r from-cyan-400 via-primary to-violet-400" />
        <CardContent className="space-y-6 p-5 sm:p-6">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-[1fr_1fr_1.3fr]">
            <label className="block">
              <span className="mb-2 block text-sm font-medium">{t.timeZones.sourceDate}</span>
              <input type="date" value={state.date} onChange={(event) => update('date', event.target.value)} className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring" />
            </label>
            <label className="block">
              <span className="mb-2 block text-sm font-medium">{t.timeZones.sourceTime}</span>
              <input type="time" value={state.time} onChange={(event) => update('time', event.target.value)} className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring" />
            </label>
            <label className="block sm:col-span-2 lg:col-span-1">
              <span className="mb-2 block text-sm font-medium">{t.timeZones.sourceZone}</span>
              <input
                type="text"
                list="source-time-zone-options"
                value={sourceQuery}
                onChange={handleSourceChange}
                onBlur={canonicalizeSource}
                onKeyDown={(event) => { if (event.key === 'Enter') canonicalizeSource() }}
                className="h-10 w-full rounded-md border border-input bg-background px-3 font-mono text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
              />
              <datalist id="source-time-zone-options">
                {CURATED_TIME_ZONES.map((entry) => <option key={entry.zone} value={entry.zone}>{locale === 'ru' ? entry.ru : entry.en}</option>)}
              </datalist>
            </label>
          </div>

          {resolution.status === 'gap' && (
            <div role="alert" className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm">
              <strong>{t.timeZones.gapTitle}</strong> {t.timeZones.gapDescription}
            </div>
          )}
          {resolution.status === 'invalid' && (
            <div role="alert" className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm">
              {t.timeZones.invalidInput}
            </div>
          )}
          {resolution.status === 'ambiguous' && (
            <fieldset className="rounded-md border border-amber-500/30 bg-amber-500/10 p-4">
              <legend className="px-1 text-sm font-semibold">{t.timeZones.foldTitle}</legend>
              <p className="mb-3 text-sm text-muted-foreground">{t.timeZones.foldDescription}</p>
              <div className="flex flex-wrap gap-2">
                {(['earlier', 'later'] as const).map((choice) => (
                  <label key={choice} className="flex items-center gap-2 rounded-md border bg-background px-3 py-2 text-sm">
                    <input type="radio" name="disambiguation" checked={state.disambiguation === choice} onChange={() => update('disambiguation', choice)} />
                    {choice === 'earlier' ? t.timeZones.earlier : t.timeZones.later}
                  </label>
                ))}
              </div>
            </fieldset>
          )}

          <div>
            <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold">{t.timeZones.comparisonTitle}</h2>
                <p className="mt-1 text-sm text-muted-foreground">{t.timeZones.comparisonHint}</p>
              </div>
              <Button type="button" variant="outline" size="sm" onClick={copyShareLink} disabled={resolution.status !== 'ready' && resolution.status !== 'ambiguous'}>
                {shareStatus === 'copied' ? t.common.copied : t.timeZones.copyLink}
              </Button>
            </div>

            <div className="space-y-2">
              {state.zones.map((zone, index) => (
                <div key={zone} className="grid gap-3 rounded-lg border bg-background/70 p-3 sm:grid-cols-[minmax(180px,1fr)_minmax(220px,1.2fr)_auto] sm:items-center">
                  <div>
                    <div className="font-medium">{zoneLabel(zone)}</div>
                    <div className="font-mono text-xs text-muted-foreground">{zone}</div>
                  </div>
                  <div className="tabular-nums">
                    {instant === null ? (
                      <span className="text-sm text-muted-foreground">—</span>
                    ) : (
                      <>
                        <div className="font-mono text-base font-semibold">{formatInstant(instant, zone, localeCode(locale))}</div>
                        <div className="mt-0.5 text-xs text-muted-foreground">{formatOffset(instant, zone)}</div>
                      </>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <IconButton label={`${t.timeZones.moveUp}: ${zone}`} disabled={index === 0} onClick={() => moveZone(index, -1)}>↑</IconButton>
                    <IconButton label={`${t.timeZones.moveDown}: ${zone}`} disabled={index === state.zones.length - 1} onClick={() => moveZone(index, 1)}>↓</IconButton>
                    <IconButton label={`${t.timeZones.remove}: ${zone}`} disabled={state.zones.length <= 2} onClick={() => removeZone(zone)}>×</IconButton>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-lg border bg-secondary/30 p-4">
            <h2 className="text-sm font-semibold">{t.timeZones.addZone}</h2>
            <div className="mt-3 flex flex-col gap-2 sm:flex-row">
              <input
                id="time-zone-city-search"
                type="search"
                list="time-zone-cities"
                value={zoneQuery}
                onChange={(event: ChangeEvent<HTMLInputElement>) => setZoneQuery(event.target.value)}
                onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); addZone() } }}
                placeholder={t.timeZones.searchPlaceholder}
                aria-label={t.timeZones.addZone}
                disabled={state.zones.length >= 5}
                className="h-10 min-w-0 flex-1 rounded-md border border-input bg-background px-3 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-50"
              />
              <datalist id="time-zone-cities">
                {CURATED_TIME_ZONES.filter((entry) => !state.zones.includes(entry.zone)).map((entry) => (
                  <option key={entry.zone} value={entry.zone}>{entry.ru} / {entry.en}</option>
                ))}
              </datalist>
              <Button type="button" onClick={addZone} disabled={!zoneQuery.trim() || state.zones.length >= 5}>{t.timeZones.add}</Button>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">{t.timeZones.searchHint}</p>
          </div>
        </CardContent>
      </Card>

      <section className="mt-8 grid gap-4 lg:grid-cols-2">
        <div className="rounded-lg border bg-card p-5">
          <h2 className="text-xl font-semibold">{t.timeZones.howTitle}</h2>
          <ol className="mt-3 list-decimal space-y-2 pl-5 text-sm leading-6 text-muted-foreground">
            {t.timeZones.howTo.map((step) => <li key={step}>{step}</li>)}
          </ol>
        </div>
        <div className="rounded-lg border bg-card p-5">
          <h2 className="text-xl font-semibold">{t.timeZones.accuracyTitle}</h2>
          <p className="mt-3 text-sm leading-6 text-muted-foreground">{t.timeZones.accuracyDescription}</p>
        </div>
      </section>

      <section className="mt-8">
        <h2 className="text-xl font-semibold">{t.timeZones.faqTitle}</h2>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {t.timeZones.faq.map((item) => (
            <article key={item.question} className="rounded-lg border bg-card p-5">
              <h3 className="font-semibold">{item.question}</h3>
              <p className="mt-2 text-sm leading-6 text-muted-foreground">{item.answer}</p>
            </article>
          ))}
        </div>
      </section>
    </div>
  )
}

interface IconButtonProps {
  label: string
  disabled: boolean
  onClick: () => void
  children: string
}

function IconButton({ label, disabled, onClick, children }: IconButtonProps) {
  return <Button type="button" variant="ghost" size="sm" aria-label={label} title={label} disabled={disabled} onClick={onClick}>{children}</Button>
}
